import { Link, useNavigate } from "react-router";
import { useDateParts } from "../../hooks/useDateParts";

import { contentLabels, contentPaths, DEMO_COHORT_IDS } from "../../utils/static";
import clsx from "clsx";
import { IAssignedTask } from "../../types/generalTypes";
import { slugify } from "../../utils/urlConversion";
import { useSqids } from "../../hooks/useSqids";
import ItemCompletion from "./ItemCompletion";
import { isPast, isBefore, parseISO } from "date-fns";
import JoinLiveButton from "../../components/JoinLiveButton";
import {
  ArrowUp,
  MonitorPlay,
  Lock,
  Code,
  X,
  BookOpen,
  CodeBlock,
  VideoCamera,
  Play,
} from "@phosphor-icons/react";
import { useAuth } from "../../hooks/useAuth";
import { getCustomWeekRange } from "../../utils/common";
import { useState } from "react";
import { ScoreMap } from "./CurrentWeek";
import ReviewAction from "../project-review/ReviewAction";

const TAG_CONFIG = {
  UNIT: {
    accent: "border-l-myGreen",
    tagColor: "text-myGreen",
    Icon: BookOpen,
  },
  PROJECT: {
    accent: "border-l-myOrange",
    tagColor: "text-myOrange",
    Icon: CodeBlock,
  },
  LIVE_EVENT: {
    accent: "border-l-myGreen",
    tagColor: "text-myGreen",
    Icon: VideoCamera,
  },
} as const;

function useTaskLink(item: IAssignedTask) {
  const sqids = useSqids();
  const { isFulltimeSelfUser } = useAuth();

  if (item.tag === "UNIT") {
    if (isFulltimeSelfUser && !isPast(item.planning_date)) return null;
    return `/${contentPaths[item.tag]}/${slugify(item.name)}-${sqids.encode(item.id)}`;
  }

  if (item.tag === "PROJECT" && isPast(item.planning_date)) {
    if (item.environment === "GITHUB" && item.project_url) {
      return item.project_url;
    }
    if (item.environment === "SANDPACK") {
      return `/${contentPaths[item.tag]}/${slugify(item.infos?.title || "")}-${sqids.encode(item.content_id)}`;
    }
  }

  return null;
}

export default function TaskItem({
  item,
  showCompletion = true,
  showFullDate = true,
  scoreMap,
  isResume = false,
}: {
  item: IAssignedTask;
  showCompletion?: boolean;
  showFullDate?: boolean;
  scoreMap?: ScoreMap;
  isResume?: boolean;
}) {
  const { hours, dayWithZero, monthWithZero } = useDateParts(item.planning_date);
  const { user } = useAuth();
  const isDemoCohort = DEMO_COHORT_IDS.includes(user?.cohort_id ?? 0);
  const sqids = useSqids();
  const navigate = useNavigate();

  const taskLink = useTaskLink(item);
  const isExternal = taskLink?.startsWith("http");
  const displayName = item.tag === "PROJECT" ? item.infos?.title || "" : item.name;
  const config = TAG_CONFIG[item.tag] || TAG_CONFIG.UNIT;

  // Score for progress bar
  const type = item.tag === "UNIT" ? "PRACTICE" : item.tag;
  const key = `${item.content_id}_${type}`;
  const score = scoreMap?.[key]?.score ?? 0;
  const isCompleted = score >= 79;
  const isLocked = item.tag === "PROJECT" && item.projectLocked === true;

  // Çözümler badge
  // Bitirme projelerinde (reviewEnabled) referans cozum yayinlanmiyor → rozet yok.
  let solutionBadge: React.ReactNode = null;
  if (item.tag === "PROJECT" && !item.reviewEnabled && isPast(item.planning_date)) {
    const weekRange = getCustomWeekRange();
    const weekStart = parseISO(weekRange.start);
    const planDate = typeof item.planning_date === "string" ? parseISO(item.planning_date) : new Date(item.planning_date);
    // Paced ogrencide backend solutionOpen bayragini saglar (pace ilerlemesine gore).
    // Saglanmissa onu kullan; aksi halde (non-paced) eski haftalik mantik degismeden kalir.
    const isSolutionOpen =
      (item.solutionOpen ?? null) !== null
        ? item.solutionOpen
        : isBefore(planDate, weekStart);

    solutionBadge = isSolutionOpen ? (
      <Link
        to={`/proje-cozumleri/${slugify(item.infos?.title || "")}-${sqids.encode(item.content_id)}`}
        className="inline-flex items-center gap-1 text-[11px] bg-myOrange/10 hover:bg-myOrange/20 text-myOrange/70 hover:text-myOrange px-2 py-0.5 rounded transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <Code size={12} weight="bold" />
        Çözümler
      </Link>
    ) : (
      <SolutionLockedBadge />
    );
  }

  const handleClick = () => {
    if (isLocked) return;
    if (!taskLink) return;
    if (isExternal) {
      window.open(taskLink, "_blank", "noopener,noreferrer");
    } else {
      navigate(taskLink);
    }
  };

  return (
    <div
      className={clsx(
        "group rounded-xl transition-all",
        isResume
          ? "bg-myGreen/[0.08] ring-1 ring-myGreen/40 hover:bg-myGreen/[0.12]"
          : "bg-white/[0.03] hover:bg-white/[0.06]",
        taskLink && !isLocked && "cursor-pointer",
        isLocked && "opacity-50",
        isCompleted && !isResume && "opacity-60"
      )}
      onClick={handleClick}
    >
      <div className={clsx("border-l-[3px] rounded-l-xl px-3.5 py-3 flex items-center gap-3", config.accent)}>
        {/* Icon */}
        <div className={clsx("shrink-0", config.tagColor)}>
          <config.Icon size={20} weight="duotone" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={clsx("text-[10px] font-bold uppercase tracking-wider", config.tagColor)}>
              {contentLabels[item.tag]}
            </span>
            {!isDemoCohort && showFullDate && (
              <span className="text-white/20 text-[10px] font-mono">
                {`${dayWithZero}/${monthWithZero}`}
              </span>
            )}
          </div>
          <p className={clsx(
            "font-medium text-sm leading-snug transition-colors truncate",
            taskLink && item.tag === "PROJECT" && "group-hover:text-myOrange",
            taskLink && item.tag !== "PROJECT" && "group-hover:text-myGreen"
          )}>
            {displayName}
            {item.tag === "LIVE_EVENT" && (
              <span className="text-white/30 font-normal"> · {item.duration} dk</span>
            )}
            <span className="text-white/0 group-hover:text-white/25 text-[10px] font-normal ml-1.5 transition-colors">
              student_id: {user?.id}
            </span>
          </p>

        </div>

        {/* Right side: score / solution / live button */}
        <div className="shrink-0 flex items-center gap-2">
          {isResume && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-myGreen/15 text-myGreen px-2 py-1 rounded-lg whitespace-nowrap group-hover:bg-myGreen/25 transition-colors">
              <Play size={12} weight="fill" />
              Devam Et
            </span>
          )}

          {item.tag === "PROJECT" && isLocked && (
            <ProjectLockedBadge lockedBy={item.lockedBy || ""} />
          )}

          {item.tag === "PROJECT" && item.environment === "GITHUB" && item.reviewEnabled && !isLocked && (
            <ReviewAction projectId={Number(item.content_id)} score={score ?? null} />
          )}

          {solutionBadge}

          {item.tag !== "LIVE_EVENT" && showCompletion && (
            <ItemCompletion
              data={{ content_id: item.content_id, type: type as any }}
              preloadedScore={score || undefined}
            />
          )}

          {item.tag === "LIVE_EVENT" && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {item.record_url && (
                <a
                  href={item.record_url}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-white/40 hover:text-white transition-colors cursor-pointer"
                >
                  <MonitorPlay size={18} />
                </a>
              )}
              <JoinLiveButton duration={item.duration} startDate={item.planning_date}>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="bg-myGreen text-black px-3 py-1 font-semibold rounded-lg inline-flex items-center gap-1 text-sm"
                >
                  Katıl
                  <span className="rotate-90">
                    <ArrowUp size={14} weight="bold" className="animate-bounce" />
                  </span>
                </a>
              </JoinLiveButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SolutionLockedBadge() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 text-white/25 hover:text-white/40 px-2 py-0.5 rounded transition-colors cursor-pointer"
      >
        <Lock size={10} weight="bold" />
        Çözümler
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="panel border rounded-2xl p-6 max-w-sm mx-4 text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            <div className="w-12 h-12 rounded-full bg-myOrange/10 flex items-center justify-center mx-auto mb-4">
              <Lock size={24} className="text-myOrange/60" />
            </div>

            <h3 className="text-lg font-semibold mb-2">Çözümler Henüz Açılmadı</h3>
            <p className="text-white/40 text-sm leading-relaxed">
              Bu projenin çözümleri bir sonraki hafta otomatik olarak açılacak.
              O zamana kadar kendi çözümünü geliştirmeye devam et!
            </p>

            <button
              onClick={() => setOpen(false)}
              className="mt-5 px-5 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-sm transition-colors cursor-pointer"
            >
              Tamam
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function ProjectLockedBadge({ lockedBy }: { lockedBy: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 text-white/25 hover:text-white/40 px-2 py-0.5 rounded transition-colors cursor-pointer"
      >
        <Lock size={10} weight="bold" />
        Kilitli
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
          <div className="bg-surface-1 border border-white/10 rounded-2xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Bu proje henüz kilitli</h3>
              <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white/60 cursor-pointer">
                <X size={18} />
              </button>
            </div>
            <p className="text-white/50 text-sm">
              {lockedBy
                ? <>Önce <strong className="text-white/80">{lockedBy}</strong> projesini tamamlayıp incelemeye göndermelisin. Onaylandığında bu proje açılır.</>
                : <>Önceki bitirme projen onaylandığında bu proje açılır.</>}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
